import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaMicrophone,
  FaMobileAlt,
  FaUsers,
  FaCircle,
  FaStepForward,
  FaStepBackward,
  FaStop,
  FaCheck,
  FaExclamationTriangle,
  FaCloudUploadAlt,
  FaDownload,
  FaFileContract,
  FaRedo,
  FaSearch,
  FaCamera,
  FaPlay,
  FaSync,
} from "react-icons/fa";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import StatusBadge from "../../../components/StatusBadge";
import EmptyState from "../../../components/EmptyState";
import { useToast } from "../../../components/Toast";
import {
  colors,
  spacing,
  typography,
  radius,
  shadows,
  zIndex,
  transitions,
  cardBase,
  inputBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
} from "../../../utils/designTokens";
import {
  actaAdmin,
  asambleaAdmin,
  codigoDeError,
  detallesDeError,
  enVentana,
  esMixtaUsaZoom,
  esOtroGrabando,
  esSalaGrabando,
  esWorkerApagado,
  horaMontevideo,
  mensajeDeError,
  salaCheckin,
  salaConfirmarChunk,
  salaEstado,
  salaFinalizarSesion,
  salaHueco,
  salaIniciarSesion,
  salaLatido,
  salaMarcador,
  salaUrlChunk,
  salaUrlPoder,
  subirAUrlFirmada,
  ventanaModoSala,
} from "../../../utils/asambleas/api";
import {
  cerrarStream,
  crearColaMarcadores,
  crearGrabadora,
  crearMedidor,
  crearWakeLock,
  elegirFormato,
  entradaDelStream,
  idDispositivo,
  listarEntradas,
  motivoErrorMicrofono,
  pedirMicrofono,
  relojReal,
  streamVivo,
  sugerirEntrada,
} from "../../../utils/asambleas/grabadora";

// Modo Sala (Asambleas, Fase 4): pantalla completa para el día de una asamblea
// presencial o mixta, pensada para un celular o una tablet con micrófono
// externo, de pie en la sala (botones de 48 px o más, contraste alto).
//
// Pasos:
//   1. Chequeo previo: permiso del micrófono, elegir la entrada (se sugiere la
//      externa), medidor de nivel (AnalyserNode), pantalla siempre encendida
//      (Wake Lock) y el aviso del cargador.
//   2. Check-in por unidad del padrón: presente / representada (con la foto del
//      poder, desde la cámara) / ausente, con el quórum en vivo por cabezas y
//      por valor (lo calcula el servidor: salaEstado).
//   3. Guion de apertura con el aviso de grabación y el orden del día.
//   4. Grabar (salaIniciarSesion + grabadora.js).
//   5. Durante la sesión: "Siguiente punto" (marcador con el tiempo desde el
//      inicio de la grabación) con el resultado opcional, tiempo transcurrido,
//      nivel y estado de la subida. El check-in sigue abierto para los que
//      llegan tarde.
//   6. Finalizar: espera el "100 % subido", cierra la sesión y ofrece
//      "Procesar acta" (actaAdmin procesar, Fase 2).
// En una mixta la fuente de audio es Zoom: no se graba acá, solo check-in y
// marcadores (sin sesión: tMs cuenta desde "Comenzar la asamblea"). El quórum
// suma a los conectados por Zoom (cabezas.remotas); con link manual el servidor
// no los ve (soloSala) y la pantalla y el guion hablan de "la sala", sin decir
// que no hay quórum. Los marcadores esperan en una cola si se corta la red y
// llevan cuánto hace que se tocaron (atrasoMs): se ubican en la hora del toque.
//
// Si el micrófono se corta (se desenchufó el receptor, iOS terminó la pista),
// lo grabado se sigue subiendo y "Seguir grabando" abre una sesión nueva (con
// continuaDe: la cortada sigue subiendo y el servidor no la cierra). Si la captura
// se pausa (iOS al bloquear la pantalla, otra app, una llamada) más de 5 s, la
// grabadora lo anota como hueco (salaHueco) y queda un aviso fijo con los
// minutos sin audio, para pedir que repitan lo importante. Una recarga de la
// página también abre una sesión nueva: el servidor usa lo que ya se había
// subido. Si otra persona del equipo ya graba desde otro dispositivo
// (grabandoAhora de salaEstado), grabar pide confirmación (el acta tendría el
// audio repetido) y el servidor exige `forzar` ("otro_grabando"); con la misma
// cuenta en otro equipo lo pregunta el servidor, que los distingue por el
// dispositivoId de este navegador (idDispositivo). Mientras se graba o sube,
// salir de la página pide confirmación y la ruta no cierra la sesión por
// inactividad (onOcupado).
// Si la red no vuelve, "Descargar una copia" baja cada sesión entera (las
// partes en orden) para subirla después.
//
// Grabación externa (un grabador aparte, el Zoom H1): se sube desde el detalle
// de la asamblea, sección Acta → "Subir audio" (Fase 2: actaAdmin subirAudio +
// registrarAudio). Esa subida es un PUT entero, no resumible: para archivos de
// más de 500 MB conviene la subida resumible de Cloud Storage (pendiente); por
// ahora la pantalla aconseja subirlos desde una computadora con buena conexión.
//
// Props:
//   aid        id de la asamblea
//   onSalir    () => void   volver al panel
//   onOcupado  (bool) => void   true mientras hay una grabación, subidas o
//              marcadores pendientes, o la asamblea está en curso
//   deps       (tests) { MediaRecorder, mediaDevices, AudioContext, fetch,
//              reloj, navigator, documento, ventana, almacen }: por defecto los
//              del navegador (almacen: el localStorage del dispositivoId)

const PASOS = ["chequeo", "checkin", "guion", "grabar", "sesion", "final"];
const RESULTADOS = ["aprobado", "rechazado", "sin_votacion", "postergado"];
const BADGE_RESULTADO = { aprobado: "aprobado", rechazado: "rechazado", sin_votacion: "cerrada", postergado: "pausado" };
const ESTADOS_CHECKIN = ["presente", "representada", "ausente"];
const BADGE_CHECKIN = { presente: "presente", representada: "en_proceso", ausente: "ausente" };
const ESTADOS_ASAMBLEA_SALA = new Set(["convocada", "en_curso"]);
const MODALIDADES_SALA = new Set(["presencial", "mixta"]);

const POLL_ESTADO_MS = 20000;
const TICK_SESION_MS = 1000;
const TICK_RESTO_MS = 30000;
const NIVEL_MS = 400;
const MAX_FOTO_BYTES = 8 * 1024 * 1024;
// Una sesión de otro dispositivo cuenta como "grabando" si latió hace menos de esto.
const LATIDO_VIVO_MS = 60000;
// Huecos que se listan en el aviso (el resto se cuenta).
const HUECOS_A_LA_VISTA = 3;

// Callables que usa la grabadora (cada uno con el objeto del callable).
const API_GRABADORA = { salaIniciarSesion, salaUrlChunk, salaConfirmarChunk, salaLatido, salaFinalizarSesion, salaHueco };

/** Lo del navegador que usa el Modo Sala (los tests pasan fakes por `deps`). */
function depsDelNavegador() {
  const w = typeof window !== "undefined" ? window : null;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  return {
    MediaRecorder: w ? w.MediaRecorder : undefined,
    mediaDevices: nav ? nav.mediaDevices : undefined,
    AudioContext: w ? w.AudioContext || w.webkitAudioContext : undefined,
    fetch: w && typeof w.fetch === "function" ? w.fetch.bind(w) : undefined,
    reloj: relojReal,
    navigator: nav,
    documento: typeof document !== "undefined" ? document : null,
    ventana: w,
  };
}

const visible = (doc) => !doc || doc.visibilityState !== "hidden";

/** "1:02:05" o "02:05". */
export function formatoDuracion(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dos = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${dos(m)}:${dos(s)}` : `${dos(m)}:${dos(s)}`;
}

const sinAcentos = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Unidades para el check-in, ordenadas por etiqueta: [{ id, etiqueta,
 * titulares: [nombre], alicuota }]. Si salaEstado trae el padrón (padron |
 * padron.unidades | unidades) se usa ese (incluye unidades sin propietario);
 * si no, se arma con las unidades de las citaciones del detalle.
 */
export function unidadesParaCheckin(estadoSala, detalle) {
  const e = estadoSala || {};
  let crudo = null;
  if (Array.isArray(e.padron)) crudo = e.padron;
  else if (e.padron && Array.isArray(e.padron.unidades)) crudo = e.padron.unidades;
  else if (Array.isArray(e.unidades)) crudo = e.unidades;
  const porId = new Map();
  const agregar = (id, etiqueta, titular, alicuota) => {
    if (id == null || id === "") return;
    const k = String(id);
    const u = porId.get(k) || { id: k, etiqueta: null, titulares: [], alicuota: null };
    if (!u.etiqueta && etiqueta) u.etiqueta = String(etiqueta);
    if (titular && !u.titulares.includes(titular)) u.titulares.push(titular);
    if (alicuota != null && alicuota !== "" && Number.isFinite(Number(alicuota))) u.alicuota = Number(alicuota);
    porId.set(k, u);
  };
  if (crudo) {
    crudo.forEach((u) => {
      if (!u || typeof u !== "object") return;
      const id = u.unidadId != null ? u.unidadId : u.aptId != null ? u.aptId : u.id;
      agregar(id, u.etiqueta, null, u.alicuota);
      const titulares = Array.isArray(u.titulares) ? u.titulares : [];
      titulares.forEach((x) => {
        const nombre = typeof x === "string" ? x : x && (x.nombre || x.titularNombre);
        if (nombre) agregar(id, null, String(nombre), null);
      });
      if (u.titularNombre) agregar(id, null, String(u.titularNombre), null);
    });
  } else {
    const invitaciones = detalle && Array.isArray(detalle.invitaciones) ? detalle.invitaciones : [];
    invitaciones.forEach((inv) =>
      (Array.isArray(inv && inv.unidades) ? inv.unidades : []).forEach((u) => {
        if (!u) return;
        agregar(u.aptId != null ? u.aptId : u.id, u.etiqueta, inv.nombre ? String(inv.nombre) : null, null);
      })
    );
  }
  return [...porId.values()]
    .map((u) => ({ ...u, etiqueta: u.etiqueta || u.id }))
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, "es", { numeric: true }));
}

/** Check-ins de salaEstado (lista o mapa por unidad) → { [unidadId]: { estado, representante, conPoder } }. */
export function checkinsDe(estadoSala) {
  const crudo = estadoSala && estadoSala.checkins;
  const out = {};
  const poner = (id, c) => {
    if (id == null || !c || !ESTADOS_CHECKIN.includes(c.estado)) return;
    out[String(id)] = {
      estado: c.estado,
      representante: c.representante && typeof c.representante === "object" ? c.representante : null,
      conPoder: !!(c.poderPath || c.conPoder || c.tienePoder),
    };
  };
  if (Array.isArray(crudo)) crudo.forEach((c) => c && poner(c.unidadId != null ? c.unidadId : c.id, c));
  else if (crudo && typeof crudo === "object") Object.entries(crudo).forEach(([id, c]) => poner(c && c.unidadId != null ? c.unidadId : id, c));
  return out;
}

/**
 * Quórum de salaEstado → { presentes, total, valorPct, umbral (%), alcanzado,
 * remotas, soloSala } o null. En una mixta, presentes ya incluye a las
 * unidades conectadas por Zoom (remotas); con link manual el servidor no las
 * ve y manda soloSala: cuenta solo la sala.
 */
export function quorumDe(estadoSala) {
  const q = estadoSala && estadoSala.quorum;
  if (!q || typeof q !== "object") return null;
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const cab = q.cabezas || {};
  const val = q.valor || {};
  const valorTotal = num(val.total);
  let umbral = q.umbral;
  if (umbral && typeof umbral === "object") umbral = umbral.valor != null ? umbral.valor : umbral.porcentaje;
  umbral = umbral != null && Number.isFinite(Number(umbral)) ? Number(umbral) : null;
  if (umbral != null && umbral > 0 && umbral <= 1) umbral *= 100;
  return {
    presentes: num(cab.presentes),
    total: num(cab.total),
    valorPct: valorTotal > 0 ? (num(val.presente) / valorTotal) * 100 : 0,
    umbral,
    alcanzado: q.alcanzado === true,
    remotas: Math.max(0, Math.floor(num(cab.remotas))),
    soloSala: q.soloSala === true || estadoSala.soloSala === true,
  };
}

/**
 * Sesiones de grabación que están grabando en OTRO dispositivo (no las de esta
 * pantalla): { ajenas, propias }. Ajenas: las abrió otra persona del equipo
 * (grabar también desde acá duplica el audio del acta: se pide confirmación).
 * Propias: las abrió este mismo usuario (una recarga de la página, que el
 * servidor cierra sola al abrir la nueva; si es otro equipo con la misma
 * cuenta, el servidor responde "otro_grabando"). Grabando: grabandoAhora de
 * salaEstado (la regla del servidor: ni cortada, ni parada a pedido subiendo
 * lo último, ni sin latido hace más de un minuto); con un servidor que no lo
 * manda, estado "grabando", sin cortar y con un latido de hace menos de un minuto.
 */
export function grabandoEnOtroDispositivo(sesiones, misSesiones, ahoraMs) {
  const mias = misSesiones instanceof Set ? misSesiones : new Set(misSesiones || []);
  const grabando = (s) =>
    typeof s.grabandoAhora === "boolean"
      ? s.grabandoAhora
      : s.estado === "grabando" &&
        // Cortada (el dispositivo avisó que ya no captura): no está grabando.
        s.interrumpida !== true &&
        Number(s.ultimoLatidoEnMs) > 0 &&
        ahoraMs - Number(s.ultimoLatidoEnMs) < LATIDO_VIVO_MS;
  const vivas = (Array.isArray(sesiones) ? sesiones : []).filter((s) => s && !mias.has(idSesion(s)) && grabando(s));
  return { ajenas: vivas.filter((s) => s.propia !== true), propias: vivas.filter((s) => s.propia === true) };
}

/**
 * Huecos de las grabaciones de esta pantalla (ratos en que la captura estuvo en
 * pausa) en el tiempo de la asamblea que muestra el reloj: [{ desdeMs, hastaMs }]
 * desde `baseMs` (cuando empezó la asamblea en esta pantalla), en orden.
 */
export function huecosDeLaAsamblea(estados, baseMs) {
  const out = [];
  (estados || []).forEach((s) => {
    if (!s || !Array.isArray(s.huecos) || !(Number(s.iniciadaEnMs) > 0)) return;
    const corrimiento = baseMs != null && Number.isFinite(Number(baseMs)) ? Number(s.iniciadaEnMs) - Number(baseMs) : 0;
    s.huecos.forEach((h) => {
      out.push({ desdeMs: Math.max(0, h.desdeMs + corrimiento), hastaMs: Math.max(0, h.hastaMs + corrimiento) });
    });
  });
  return out.sort((a, b) => a.desdeMs - b.desdeMs);
}

/**
 * Punto en curso y resultados anotados según los marcadores ya guardados (una
 * recarga de la página): el último "inicio_punto" y el último resultado de cada punto.
 */
export function restaurarDeMarcadores(marcadores, puntos) {
  const lista = (Array.isArray(marcadores) ? marcadores : []).filter((m) => m && typeof m === "object");
  // La hora del toque (marcadoEnMs: un marcador que esperó un corte de red llega tarde); si no, la de llegada.
  const orden = (m) => Number(m.marcadoEnMs) || Number(m.enMs) || 0;
  const indice = new Map((puntos || []).map((p, i) => [String(p.id), i]));
  let puntoActual = -1;
  let ultimo = -Infinity;
  const resultados = {};
  const resultadosEn = {};
  lista.forEach((m) => {
    const i = indice.get(String(m.puntoId));
    if (i == null) return;
    if (m.tipo === "inicio_punto" && orden(m) >= ultimo) {
      ultimo = orden(m);
      puntoActual = i;
    }
    if (m.tipo === "resultado" && RESULTADOS.includes(m.resultado) && orden(m) >= (resultadosEn[m.puntoId] || -Infinity)) {
      resultadosEn[m.puntoId] = orden(m);
      resultados[String(m.puntoId)] = m.resultado;
    }
  });
  return { puntoActual, resultados };
}

/** Suma de la subida de todas las sesiones de esta pantalla. */
export function resumenSubida(estados) {
  const lista = estados || [];
  let objetos = 0;
  let confirmados = 0;
  let bytesTotales = 0;
  let bytesConfirmados = 0;
  lista.forEach((s) => {
    objetos += s.objetos;
    confirmados += s.confirmados;
    bytesTotales += s.bytesTotales;
    bytesConfirmados += s.bytesConfirmados;
  });
  const abiertas = lista.filter((s) => s.fase !== "completa");
  const todoSubido = lista.length > 0 && objetos > 0 && lista.every((s) => s.todoSubido || s.fase === "completa");
  const porcentaje = todoSubido ? 100 : bytesTotales > 0 ? Math.min(99, Math.floor((bytesConfirmados * 100) / bytesTotales)) : 0;
  return {
    objetos,
    confirmados,
    pendientes: objetos - confirmados,
    porcentaje,
    todoSubido,
    completas: lista.filter((s) => s.fase === "completa").length,
    sinConexion: abiertas.some((s) => s.sinConexion),
    conError: abiertas.some((s) => s.conError),
    sesionRechazada: abiertas.some((s) => s.sesionRechazada),
  };
}

const idSesion = (s) => String((s && (s.sesionId || s.id)) || "");

// ── Foto del poder ──

function errorFoto() {
  const e = new Error("foto");
  e.motivoFoto = true;
  return e;
}

/** Decodifica una imagen y la pasa a JPEG con el lado mayor ≤ `lado` px (null si el navegador no puede). */
async function aJpeg(archivo, lado, calidad) {
  if (typeof document === "undefined") return null;
  let fuente = null;
  let ancho = 0;
  let alto = 0;
  let liberar = () => {};
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(archivo);
      fuente = bmp;
      ancho = bmp.width;
      alto = bmp.height;
      liberar = () => bmp.close && bmp.close();
    } catch (_) {
      fuente = null;
    }
  }
  if (!fuente && typeof URL !== "undefined" && typeof URL.createObjectURL === "function") {
    const url = URL.createObjectURL(archivo);
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = url;
      });
      fuente = img;
      ancho = img.naturalWidth;
      alto = img.naturalHeight;
    } catch (_) {
      fuente = null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  if (!fuente || !ancho || !alto) return null;
  const escala = Math.min(1, lado / Math.max(ancho, alto));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(ancho * escala));
  canvas.height = Math.max(1, Math.round(alto * escala));
  const ctx = canvas.getContext ? canvas.getContext("2d") : null;
  if (!ctx) return null;
  ctx.drawImage(fuente, 0, 0, canvas.width, canvas.height);
  liberar();
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", calidad));
}

/**
 * Foto del poder lista para subir: JPEG o PNG de hasta 8 MB tal cual; otro
 * formato (HEIC, WebP) o una foto más pesada se pasa a JPEG más chico.
 * @returns {Promise<{blob: Blob, contentType: string}>}
 */
export async function prepararFotoPoder(archivo) {
  const tipo = String((archivo && archivo.type) || "").toLowerCase();
  if ((tipo === "image/jpeg" || tipo === "image/png") && archivo.size <= MAX_FOTO_BYTES) return { blob: archivo, contentType: tipo };
  for (const [lado, calidad] of [
    [2400, 0.85],
    [1600, 0.75],
  ]) {
    const blob = await aJpeg(archivo, lado, calidad);
    if (!blob) break;
    if (blob.size <= MAX_FOTO_BYTES) return { blob, contentType: "image/jpeg" };
  }
  throw errorFoto();
}

const crearVista = (archivo) => {
  try {
    return typeof URL !== "undefined" && typeof URL.createObjectURL === "function" ? URL.createObjectURL(archivo) : null;
  } catch (_) {
    return null;
  }
};
const revocarVista = (url) => {
  try {
    if (url && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(url);
  } catch (_) {
    /* nada */
  }
};

// ─────────────────────────────── Pantalla ───────────────────────────────

export default function ModoSala({ aid, onSalir, onOcupado, deps: depsProp }) {
  const { t, i18n } = useTranslation("asambleas");
  const { showToast } = useToast();
  const deps = useMemo(() => ({ ...depsDelNavegador(), ...(depsProp || {}) }), [depsProp]);
  const reloj = deps.reloj || relojReal;
  const idioma = (i18n && i18n.language) || "es";

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  // ── Asamblea y estado de la sala ──
  const [detalle, setDetalle] = useState(null);
  const [errorCarga, setErrorCarga] = useState("");
  const [estadoSala, setEstadoSala] = useState(null);
  const [errorEstado, setErrorEstado] = useState("");
  // Check-ins recién guardados, hasta que salaEstado los traiga.
  const [checkinsLocales, setCheckinsLocales] = useState({});
  const [paso, setPaso] = useState("chequeo");
  const [ahora, setAhora] = useState(() => reloj.ahora());

  useEffect(() => {
    let vivo = true;
    asambleaAdmin("detalle", { aid })
      .then((d) => {
        if (vivo) setDetalle(d || null);
      })
      .catch((e) => {
        if (vivo) setErrorCarga(mensajeDeError(e));
      });
    return () => {
      vivo = false;
    };
  }, [aid]);

  const cargarEstado = useCallback(async () => {
    const pedidoEnMs = reloj.ahora();
    try {
      const r = await salaEstado({ aid });
      if (!montado.current) return;
      setEstadoSala(r || {});
      setErrorEstado("");
      setCheckinsLocales((prev) => {
        const quedan = Object.entries(prev).filter(([, c]) => c.enMs > pedidoEnMs);
        return quedan.length === Object.keys(prev).length ? prev : Object.fromEntries(quedan);
      });
    } catch (e) {
      if (montado.current) setErrorEstado(mensajeDeError(e));
    }
  }, [aid, reloj]);

  const asamblea = detalle && detalle.asamblea ? detalle.asamblea : null;
  const ventana = asamblea ? ventanaModoSala(asamblea) : null;
  const aplica = !!asamblea && MODALIDADES_SALA.has(asamblea.modalidad) && ESTADOS_ASAMBLEA_SALA.has(asamblea.estado);
  const enHorario = aplica && enVentana(ventana, ahora);

  // ── Reloj de la pantalla (cada segundo durante la sesión) ──
  useEffect(() => {
    let timer = null;
    let cancelado = false;
    const espera = paso === "sesion" || paso === "final" ? TICK_SESION_MS : TICK_RESTO_MS;
    const ciclo = () => {
      timer = reloj.setTimeout(() => {
        if (cancelado) return;
        setAhora(reloj.ahora());
        ciclo();
      }, espera);
    };
    ciclo();
    return () => {
      cancelado = true;
      reloj.clearTimeout(timer);
    };
  }, [paso, reloj]);

  // ── Micrófono, medidor, pantalla y batería ──
  const streamRef = useRef(null);
  const medidorRef = useRef(null);
  const [mic, setMic] = useState({ estado: "sin_probar", motivo: null, entradas: [], entradaId: null, etiqueta: "" });
  const [micVersion, setMicVersion] = useState(0);
  const [nivel, setNivel] = useState(null);

  const cerrarMicrofono = useCallback(() => {
    if (medidorRef.current) medidorRef.current.cerrar();
    medidorRef.current = null;
    if (streamRef.current) cerrarStream(streamRef.current);
    streamRef.current = null;
    setNivel(null);
  }, []);

  const usarStream = useCallback(
    (stream) => {
      cerrarMicrofono();
      streamRef.current = stream;
      const medidor = crearMedidor(stream, { AudioContext: deps.AudioContext });
      medidorRef.current = medidor;
      medidor.reanudar();
    },
    [cerrarMicrofono, deps.AudioContext]
  );

  /** Abre el micrófono (la entrada pedida o, la primera vez, la externa si hay) y devuelve el stream. */
  const abrirMicrofono = useCallback(
    async (deviceId) => {
      setMic((m) => ({ ...m, estado: "pidiendo", motivo: null }));
      try {
        usarStream(await pedirMicrofono({ mediaDevices: deps.mediaDevices, deviceId: deviceId || null }));
        const entradas = await listarEntradas(deps.mediaDevices);
        let actual = entradaDelStream(streamRef.current);
        const sugerida = sugerirEntrada(entradas);
        if (!deviceId && sugerida && sugerida.deviceId !== actual.deviceId) {
          try {
            usarStream(await pedirMicrofono({ mediaDevices: deps.mediaDevices, deviceId: sugerida.deviceId }));
            actual = entradaDelStream(streamRef.current);
          } catch (_) {
            /* se queda con la del sistema: la elige a mano */
          }
        }
        const elegida = entradas.find((x) => x.deviceId === (actual.deviceId || deviceId)) || null;
        if (montado.current) {
          setMic({
            estado: "listo",
            motivo: null,
            entradas,
            entradaId: elegida ? elegida.deviceId : actual.deviceId || deviceId || null,
            etiqueta: (elegida && elegida.label) || actual.label || "",
          });
          setMicVersion((v) => v + 1);
        }
        return streamRef.current;
      } catch (e) {
        if (montado.current) setMic((m) => ({ ...m, estado: "error", motivo: motivoErrorMicrofono(e) }));
        throw e;
      }
    },
    [deps.mediaDevices, usarStream]
  );

  useEffect(() => {
    if (!medidorRef.current || !["chequeo", "grabar", "sesion"].includes(paso)) return undefined;
    let timer = null;
    let cancelado = false;
    const ciclo = () => {
      timer = reloj.setTimeout(() => {
        if (cancelado) return;
        const m = medidorRef.current;
        setNivel(m ? m.nivelDb() : null);
        ciclo();
      }, NIVEL_MS);
    };
    ciclo();
    return () => {
      cancelado = true;
      reloj.clearTimeout(timer);
    };
  }, [micVersion, paso, reloj]);

  const wakeRef = useRef(null);
  const [, setWakeVersion] = useState(0);
  useEffect(() => {
    const w = crearWakeLock({ navigator: deps.navigator, document: deps.documento });
    wakeRef.current = w;
    const quitar = w.suscribir(() => {
      if (montado.current) setWakeVersion((v) => v + 1);
    });
    w.pedir();
    return () => {
      quitar();
      w.destruir();
      wakeRef.current = null;
    };
  }, [deps.navigator, deps.documento]);

  const [bateria, setBateria] = useState(null);
  useEffect(() => {
    const nav = deps.navigator;
    if (!nav || typeof nav.getBattery !== "function") return undefined;
    let b = null;
    let vivo = true;
    const leer = () => {
      if (vivo && b) setBateria({ cargando: !!b.charging, nivel: Number(b.level) });
    };
    Promise.resolve()
      .then(() => nav.getBattery())
      .then((x) => {
        if (!x || !vivo) return;
        b = x;
        leer();
        if (typeof x.addEventListener === "function") {
          x.addEventListener("chargingchange", leer);
          x.addEventListener("levelchange", leer);
        }
      })
      .catch(() => {});
    return () => {
      vivo = false;
      if (b && typeof b.removeEventListener === "function") {
        b.removeEventListener("chargingchange", leer);
        b.removeEventListener("levelchange", leer);
      }
    };
  }, [deps.navigator]);

  // ── Sesiones de grabación y marcadores ──
  const grabadorasRef = useRef([]);
  // Id de este navegador (idDispositivo: localStorage), el mismo en todas sus
  // grabaciones: el servidor distingue una recarga de otro equipo con la misma cuenta.
  const dispositivoRef = useRef(null);
  const [, setGrabVersion] = useState(0);
  const colaRef = useRef(null);
  const [, setColaVersion] = useState(0);
  const [iniciando, setIniciando] = useState(false);
  const [inicioAsambleaMs, setInicioAsambleaMs] = useState(null);
  const [mixtaForzada, setMixtaForzada] = useState(false);
  const [puntoActual, setPuntoActual] = useState(-1);
  const [resultados, setResultados] = useState({});

  useEffect(() => {
    const cola = crearColaMarcadores({
      enviar: (m) => salaMarcador(m),
      reloj,
      alCambiar: () => {
        if (montado.current) setColaVersion((v) => v + 1);
      },
    });
    colaRef.current = cola;
    return () => {
      cola.destruir();
      colaRef.current = null;
    };
  }, [reloj]);

  // Al salir de la pantalla: cortar todo (lo que no se subió se pierde; por eso se pide confirmación).
  useEffect(
    () => () => {
      grabadorasRef.current.forEach((g) => g.destruir());
      grabadorasRef.current = [];
      if (medidorRef.current) medidorRef.current.cerrar();
      medidorRef.current = null;
      if (streamRef.current) cerrarStream(streamRef.current);
      streamRef.current = null;
    },
    []
  );

  const grabadoras = grabadorasRef.current;
  const estados = grabadoras.map((g) => g.estado());
  const actual = grabadoras.length ? grabadoras[grabadoras.length - 1] : null;
  const estadoActual = estados.length ? estados[estados.length - 1] : null;
  const grabando = !!estadoActual && estadoActual.fase === "grabando";
  const interrumpida = !!estadoActual && estadoActual.fase === "interrumpida";
  const subida = resumenSubida(estados);
  const mixta = !!asamblea && (asamblea.modalidad === "mixta" || mixtaForzada);
  const empezada = inicioAsambleaMs != null;
  const grabacionAbierta = estados.some((s) => !["completa", "error", "inactiva"].includes(s.fase));
  const marcasPendientes = colaRef.current ? colaRef.current.pendientes() : 0;
  const errorMarcas = colaRef.current ? colaRef.current.ultimoError() : null;
  const ocupado = grabacionAbierta || marcasPendientes > 0 || (empezada && paso !== "final");

  useEffect(() => {
    if (onOcupado) onOcupado(ocupado);
  }, [ocupado, onOcupado]);
  useEffect(
    () => () => {
      if (onOcupado) onOcupado(false);
    },
    [onOcupado]
  );

  // Salir con una grabación abierta corta la grabación: el navegador lo pregunta.
  useEffect(() => {
    const v = deps.ventana;
    if (!ocupado || !v || typeof v.addEventListener !== "function") return undefined;
    const alSalir = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    v.addEventListener("beforeunload", alSalir);
    return () => v.removeEventListener("beforeunload", alSalir);
  }, [ocupado, deps.ventana]);

  // Estado de la sala: al habilitarse, cada 20 s con la pestaña visible y al volver a ella.
  useEffect(() => {
    if (enHorario) cargarEstado();
  }, [enHorario, cargarEstado]);

  useEffect(() => {
    if (!enHorario || !["checkin", "guion", "grabar", "sesion"].includes(paso)) return undefined;
    let timer = null;
    let cancelado = false;
    const ciclo = () => {
      timer = reloj.setTimeout(async () => {
        if (cancelado) return;
        if (visible(deps.documento)) await cargarEstado();
        if (!cancelado) ciclo();
      }, POLL_ESTADO_MS);
    };
    ciclo();
    return () => {
      cancelado = true;
      reloj.clearTimeout(timer);
    };
  }, [enHorario, paso, reloj, cargarEstado, deps.documento]);

  useEffect(() => {
    const doc = deps.documento;
    if (!doc || typeof doc.addEventListener !== "function") return undefined;
    const alVolver = () => {
      if (!visible(doc)) return;
      if (enHorario) cargarEstado();
      if (colaRef.current) colaRef.current.reintentarAhora();
    };
    doc.addEventListener("visibilitychange", alVolver);
    return () => doc.removeEventListener("visibilitychange", alVolver);
  }, [enHorario, cargarEstado, deps.documento]);

  // Recarga a mitad de la asamblea: retomar el punto y los resultados anotados, y
  // abrir directo en "Grabar" (la grabación anterior se usa igual).
  const restaurado = useRef(false);
  useEffect(() => {
    if (restaurado.current || !estadoSala || !asamblea) return;
    restaurado.current = true;
    const previo = restaurarDeMarcadores(estadoSala.marcadores, asamblea.ordenDelDia || []);
    if (previo.puntoActual >= 0) setPuntoActual(previo.puntoActual);
    if (Object.keys(previo.resultados).length) setResultados(previo.resultados);
    const haySesiones = Array.isArray(estadoSala.sesiones) && estadoSala.sesiones.length > 0;
    if (haySesiones || previo.puntoActual >= 0) setPaso((p) => (p === "chequeo" ? "grabar" : p));
  }, [estadoSala, asamblea]);

  // ── Check-in ──
  const unidades = useMemo(() => unidadesParaCheckin(estadoSala, detalle), [estadoSala, detalle]);
  const checkins = useMemo(() => {
    const out = checkinsDe(estadoSala);
    Object.entries(checkinsLocales).forEach(([id, c]) => {
      out[id] = c;
    });
    return out;
  }, [estadoSala, checkinsLocales]);
  const quorum = quorumDe(estadoSala);
  const [busqueda, setBusqueda] = useState("");
  const [guardandoUnidad, setGuardandoUnidad] = useState(null);
  const [rep, setRep] = useState(null); // modal "representada"

  /** Guarda el check-in de una unidad y refresca el quórum. Tira si el servidor lo rechaza. */
  const registrar = async (unidad, estado, extra = {}) => {
    const r = await salaCheckin({ aid, unidadId: unidad.id, estado, ...extra });
    const guardado = r && r.checkin && typeof r.checkin === "object" ? r.checkin : { estado, ...extra };
    setCheckinsLocales((prev) => ({
      ...prev,
      [unidad.id]: {
        estado: guardado.estado || estado,
        representante: guardado.representante || extra.representante || null,
        conPoder: !!(guardado.poderPath || extra.poderPath),
        enMs: reloj.ahora(),
      },
    }));
    cargarEstado();
  };

  const marcarUnidad = async (unidad, estado) => {
    if (estado === "representada") {
      abrirRepresentada(unidad);
      return;
    }
    setGuardandoUnidad(unidad.id);
    try {
      await registrar(unidad, estado);
    } catch (e) {
      showToast(mensajeDeError(e), "error");
    } finally {
      if (montado.current) setGuardandoUnidad(null);
    }
  };

  const abrirRepresentada = (unidad) => {
    const c = checkins[unidad.id];
    const r = c && c.estado === "representada" && c.representante ? c.representante : {};
    setRep({ unidad, nombre: r.nombre || "", documento: r.documento || "", archivo: null, vista: null, error: "", guardando: false, progreso: null });
  };

  const cerrarRepresentada = () => {
    setRep((r) => {
      if (r && r.vista) revocarVista(r.vista);
      return null;
    });
  };

  const elegirFoto = (archivo) => {
    setRep((r) => {
      if (!r) return r;
      if (r.vista) revocarVista(r.vista);
      return { ...r, archivo: archivo || null, vista: archivo ? crearVista(archivo) : null, error: "" };
    });
  };

  const guardarRepresentada = async () => {
    if (!rep || rep.guardando) return;
    const nombre = rep.nombre.trim();
    if (!nombre) {
      setRep((r) => r && { ...r, error: t("modoSala.checkin.faltaNombre") });
      return;
    }
    setRep((r) => r && { ...r, guardando: true, error: "", progreso: null });
    try {
      let poderPath = null;
      if (rep.archivo) {
        const foto = await prepararFotoPoder(rep.archivo);
        const destino = await salaUrlPoder({ aid, unidadId: rep.unidad.id, contentType: foto.contentType });
        if (!destino || !destino.uploadUrl) throw new Error(t("modoSala.checkin.errorSubirFoto"));
        await subirAUrlFirmada(destino.uploadUrl, foto.blob, {
          contentType: foto.contentType,
          headers: destino.headers || {},
          onProgreso: (pct) => setRep((r) => r && { ...r, progreso: pct }),
        });
        poderPath = destino.path || null;
      }
      await registrar(rep.unidad, "representada", { representante: { nombre, documento: rep.documento.trim() }, poderPath });
      if (montado.current) cerrarRepresentada();
    } catch (e) {
      if (montado.current) {
        setRep((r) => r && { ...r, guardando: false, error: e && e.motivoFoto ? t("modoSala.checkin.errorFoto") : mensajeDeError(e) });
      }
    }
  };

  // ── Grabar ──
  // "Otro dispositivo está grabando": null | { dispositivo, continuaDe } (continuaDe: si venía de "Seguir grabando").
  const [confirmarOtro, setConfirmarOtro] = useState(null);

  /**
   * Abre una sesión y graba. Si otra persona del equipo ya graba desde otro
   * dispositivo (según salaEstado, o porque el servidor respondió
   * "otro_grabando"), primero pide confirmación: con `forzar` graba igual.
   * continuaDe: la sesión cortada que se sigue ("Seguir grabando").
   */
  const empezarAGrabar = async ({ forzar = false, continuaDe = null } = {}) => {
    if (iniciando) return;
    if (!forzar) {
      const propiasIds = new Set(grabadorasRef.current.map((x) => x.sesionId()).filter(Boolean));
      const { ajenas } = grabandoEnOtroDispositivo(estadoSala && estadoSala.sesiones, propiasIds, reloj.ahora());
      if (ajenas.length) {
        setConfirmarOtro({ dispositivo: ajenas[ajenas.length - 1].dispositivo || null, continuaDe });
        return;
      }
    }
    setIniciando(true);
    let g = null;
    try {
      const formato = elegirFormato(deps.MediaRecorder);
      if (!formato) {
        showToast(t("modoSala.mic.error.formato"), "error");
        return;
      }
      let stream = streamRef.current;
      if (!stream || !streamVivo(stream)) {
        try {
          stream = await abrirMicrofono(mic.entradaId);
        } catch (e) {
          // La entrada elegida ya no está (se desenchufó el receptor): mejor
          // grabar con el micrófono que haya que no grabar.
          if (!mic.entradaId || motivoErrorMicrofono(e) !== "sin_microfono") throw e;
          stream = await abrirMicrofono(null);
          showToast(t("modoSala.mic.usandoOtro"), "warning");
        }
      }
      if (!dispositivoRef.current) dispositivoRef.current = idDispositivo(deps.almacen);
      g = crearGrabadora({
        aid,
        stream,
        formato,
        api: API_GRABADORA,
        deps,
        medidor: medidorRef.current,
        wakeLock: wakeRef.current,
        dispositivoId: dispositivoRef.current,
      });
      g.suscribir(() => {
        if (montado.current) setGrabVersion((v) => v + 1);
      });
      await g.iniciar({ ...(forzar ? { forzar: true } : {}), ...(continuaDe ? { continuaDe } : {}) });
      grabadorasRef.current = [...grabadorasRef.current, g];
      setInicioAsambleaMs((v) => (v == null ? reloj.ahora() : v));
      setPaso("sesion");
      setGrabVersion((v) => v + 1);
    } catch (e) {
      if (g) g.destruir();
      if (esOtroGrabando(e)) {
        // El estado de la sala estaba atrasado: el servidor vio otro dispositivo grabando.
        setConfirmarOtro({ dispositivo: detallesDeError(e).dispositivo || null, continuaDe });
        cargarEstado();
      } else if (esMixtaUsaZoom(e)) {
        setMixtaForzada(true);
        showToast(t("modoSala.errores.mixtaUsaZoom"), "warning");
      } else if (e && e.name && motivoErrorMicrofono(e) !== "otro") {
        showToast(t(`modoSala.mic.error.${motivoErrorMicrofono(e)}`), "error");
      } else {
        showToast(mensajeDeError(e), "error");
      }
    } finally {
      if (montado.current) setIniciando(false);
    }
  };

  /** Mixta: no se graba acá; "Comenzar" solo arranca el reloj de los marcadores. */
  const comenzarMixta = () => {
    setInicioAsambleaMs((v) => (v == null ? reloj.ahora() : v));
    setPaso("sesion");
  };

  /**
   * Después de un corte del micrófono: lo grabado se termina de subir (en
   * paralelo) y se abre enseguida una sesión nueva con continuaDe (la cortada:
   * el servidor no la confunde con una recarga ni la cierra a la fuerza).
   */
  const seguirGrabando = async () => {
    if (iniciando) return;
    const anterior = actual ? actual.sesionId() : null;
    if (actual) actual.finalizar().catch(() => {});
    if (streamRef.current && !streamVivo(streamRef.current)) cerrarMicrofono();
    await empezarAGrabar({ continuaDe: anterior });
  };

  // ── Marcadores ──
  const puntos = (asamblea && asamblea.ordenDelDia) || [];

  const marcar = (puntoId, tipo, resultado) => {
    if (!colaRef.current) return;
    const conSesion = !mixta && !!actual && !!estadoActual && estadoActual.fase === "grabando";
    const tMs = conSesion ? actual.tiempoMs() : inicioAsambleaMs != null ? Math.max(0, reloj.ahora() - inicioAsambleaMs) : 0;
    colaRef.current.encolar({
      aid,
      sesionId: conSesion ? estadoActual.sesionId : null,
      puntoId,
      tipo,
      ...(tipo === "resultado" ? { resultado } : {}),
      tMs,
    });
  };

  const siguientePunto = () => {
    const sig = puntoActual + 1;
    if (sig >= puntos.length) return;
    marcar(puntos[sig].id, "inicio_punto");
    setPuntoActual(sig);
  };

  const puntoAnterior = () => {
    const ant = puntoActual - 1;
    if (ant < 0) return;
    marcar(puntos[ant].id, "inicio_punto");
    setPuntoActual(ant);
  };

  const anotarResultado = (resultado) => {
    const p = puntos[puntoActual];
    if (!p) return;
    marcar(p.id, "resultado", resultado);
    setResultados((r) => ({ ...r, [String(p.id)]: resultado }));
  };

  // ── Terminar y procesar ──
  const [confirmarTerminar, setConfirmarTerminar] = useState(false);
  const [confirmarSalida, setConfirmarSalida] = useState(false);
  const [final, setFinal] = useState(null); // null | { listo, completas, total, errores, mixta, sinGrabacion }
  const [procesando, setProcesando] = useState(false);
  const [procesado, setProcesado] = useState(null); // { estado, workerHabilitado } | { aviso }
  // Confirmaciones de "Procesar acta", en el orden en que las pide el servidor:
  // otra grabación de la sala abierta (salaViva: { dispositivos, reproceso }) y
  // un acta que la IA reemplaza (confirmarReproceso: { salaGrabando }).
  const [salaViva, setSalaViva] = useState(null);
  const [confirmarReproceso, setConfirmarReproceso] = useState(null);

  const terminar = async () => {
    setConfirmarTerminar(false);
    setPaso("final");
    if (mixta) {
      setFinal({ listo: true, mixta: true });
      return;
    }
    const lista = grabadorasRef.current;
    if (!lista.length) {
      setFinal({ listo: true, sinGrabacion: true });
      return;
    }
    setFinal({ listo: false });
    await Promise.all(lista.map((g) => g.detener().catch(() => {})));
    // La grabación terminó: se suelta el micrófono (la subida no lo necesita).
    cerrarMicrofono();
    const res = await Promise.all(lista.map((g) => g.finalizar().then((r) => ({ r }), (e) => ({ e }))));
    if (!montado.current) return;
    const completas = lista.filter((g) => g.estado().fase === "completa").length;
    setFinal({
      listo: true,
      completas,
      total: lista.length,
      errores: res.filter((x) => x.e).map((x) => mensajeDeError(x.e)),
    });
  };

  /**
   * "Procesar acta". El servidor puede pedir confirmar, en este orden: que otra
   * grabación de Modo Sala sigue abierta ("sala_grabando" → confirmarSalaGrabando)
   * y que la IA reemplaza el acta existente (→ confirmarReproceso). Lo ya
   * confirmado viaja en el pedido siguiente.
   */
  const procesar = async ({ confirmarReproceso: reproceso = false, confirmarSalaGrabando = false } = {}) => {
    setConfirmarReproceso(null);
    setSalaViva(null);
    setProcesando(true);
    try {
      const r =
        (await actaAdmin("procesar", {
          aid,
          ...(reproceso ? { confirmarReproceso: true } : {}),
          ...(confirmarSalaGrabando ? { confirmarSalaGrabando: true } : {}),
        })) || {};
      if (r.requiereConfirmacion && r.motivo === "sala_grabando") {
        setSalaViva({ dispositivos: Array.isArray(r.dispositivos) ? r.dispositivos.filter((d) => typeof d === "string" && d) : [], reproceso });
        return;
      }
      if (r.requiereConfirmacion) {
        setConfirmarReproceso({ salaGrabando: confirmarSalaGrabando });
        return;
      }
      setProcesado({ estado: r.estado || "pendiente", workerHabilitado: typeof r.workerHabilitado === "boolean" ? r.workerHabilitado : null });
    } catch (e) {
      const motivo = String(detallesDeError(e).motivo || "");
      if (esWorkerApagado(e)) setProcesado({ aviso: t("modoSala.final.workerApagado") });
      else if (esSalaGrabando(e)) showToast(mensajeDeError(e), "warning");
      else if (!reproceso && codigoDeError(e) === "failed-precondition" && /reproce|confirm/i.test(motivo)) {
        setConfirmarReproceso({ salaGrabando: confirmarSalaGrabando });
      } else showToast(mensajeDeError(e), "error");
    } finally {
      if (montado.current) setProcesando(false);
    }
  };

  /** Copia local de cada sesión (los objetos en orden: un archivo que se puede subir desde el detalle). */
  const descargarCopia = () => {
    const doc = deps.documento || (typeof document !== "undefined" ? document : null);
    if (!doc || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return;
    grabadorasRef.current.forEach((g, i) => {
      const blob = g.copia();
      if (!blob || !blob.size) return;
      const url = URL.createObjectURL(blob);
      const a = doc.createElement("a");
      a.href = url;
      a.download = `asamblea-${aid}-parte-${i + 1}.${g.estado().ext || g.formato.ext}`;
      doc.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    });
  };

  const salir = () => {
    if (ocupado) setConfirmarSalida(true);
    else if (onSalir) onSalir();
  };

  const salirIgual = () => {
    setConfirmarSalida(false);
    grabadorasRef.current.forEach((g) => g.destruir());
    grabadorasRef.current = [];
    if (onOcupado) onOcupado(false);
    if (onSalir) onSalir();
  };

  // ── Navegación entre pasos ──
  const puedeIr = (p) => {
    if (p === "final") return paso === "final";
    if (paso === "final") return p === "chequeo" || p === "checkin" || p === "guion";
    if (p === "sesion") return empezada;
    return true;
  };
  const irA = (p) => {
    if (!puedeIr(p)) return;
    setPaso(p === "grabar" && empezada ? "sesion" : p);
  };

  // ── Render ──
  const encabezado = (
    <header style={S.barra}>
      <button type="button" style={{ ...btnGhost, ...S.botonIcono }} className="habitia-btn-ghost" onClick={salir} aria-label={t("modoSala.salir")}>
        <FaArrowLeft size={18} />
      </button>
      <div style={S.barraTexto}>
        <h1 style={S.titulo}>{t("modoSala.titulo")}</h1>
        {asamblea && (
          <span style={S.subtitulo}>
            {[asamblea.edificio && asamblea.edificio.nombre, asamblea.lugar].filter(Boolean).join(" · ")}
          </span>
        )}
      </div>
      {grabando && <StatusBadge status="activa" label={t("modoSala.sesion.grabandoCorto")} />}
    </header>
  );

  if (errorCarga || (detalle && !asamblea)) {
    return (
      <div style={S.pagina}>
        {encabezado}
        <main style={S.contenido}>
          <div style={S.error} role="alert">
            {errorCarga || t("modoSala.errores.sinAsamblea")}
          </div>
          <button type="button" style={{ ...btnSecondary, ...S.botonGrande }} className="habitia-btn-secondary" onClick={onSalir}>
            {t("modoSala.volverPanel")}
          </button>
        </main>
      </div>
    );
  }

  if (!asamblea) {
    return (
      <div style={S.pagina}>
        {encabezado}
        <main style={S.contenido}>
          <p style={S.texto}>{t("modoSala.cargando")}</p>
        </main>
      </div>
    );
  }

  if (!aplica) {
    const motivo = !MODALIDADES_SALA.has(asamblea.modalidad) ? "remota" : "estado";
    return (
      <div style={S.pagina}>
        {encabezado}
        <main style={S.contenido}>
          <EmptyState icon={FaUsers} title={t("modoSala.noAplica.titulo")} description={t(`modoSala.noAplica.${motivo}`)} />
          <button type="button" style={{ ...btnSecondary, ...S.botonGrande }} className="habitia-btn-secondary" onClick={onSalir}>
            {t("modoSala.volverPanel")}
          </button>
        </main>
      </div>
    );
  }

  const misSesiones = new Set(estados.map((s) => s.sesionId).filter(Boolean));
  const sesionesServidor = estadoSala && Array.isArray(estadoSala.sesiones) ? estadoSala.sesiones : [];
  const sesionesPrevias = sesionesServidor.filter((s) => !misSesiones.has(idSesion(s)));
  const otrasGrabando = grabandoEnOtroDispositivo(sesionesServidor, misSesiones, ahora);
  const otraAjena = otrasGrabando.ajenas.length ? otrasGrabando.ajenas[otrasGrabando.ajenas.length - 1] : null;
  const huecos = mixta ? [] : huecosDeLaAsamblea(estados, inicioAsambleaMs);
  const necesitaServidor = paso !== "chequeo";
  const transcurridoMs = inicioAsambleaMs != null ? Math.max(0, ahora - inicioAsambleaMs) : 0;
  // Antes de crear el Wake Lock (primer render) se lo da por soportado: el aviso
  // "no se puede" sale solo si de verdad falta.
  const wake = wakeRef.current;
  const wakeSoportado = wake ? wake.soportado : true;
  const wakeActivo = !!(wake && wake.activo());
  // iOS deja el AudioContext suspendido si se creó después del permiso (sin el
  // gesto del usuario): cualquier toque lo reanuda y el medidor vuelve a andar.
  const reanudarAudio = () => {
    if (medidorRef.current) medidorRef.current.reanudar();
  };

  return (
    <div style={S.pagina} onPointerDown={reanudarAudio}>
      {encabezado}

      <nav aria-label={t("modoSala.pasos.aria")} style={S.pasos}>
        {PASOS.map((p, i) => {
          const activo = paso === p;
          return (
            <button
              key={p}
              type="button"
              onClick={() => irA(p)}
              disabled={!puedeIr(p)}
              aria-current={activo ? "step" : undefined}
              style={{ ...S.paso, ...(activo ? S.pasoActivo : {}), ...(!puedeIr(p) ? S.pasoApagado : {}) }}
            >
              {i + 1}. {t(`modoSala.pasos.${p}`)}
            </button>
          );
        })}
      </nav>

      <main style={S.contenido}>
        {necesitaServidor && !enHorario && !grabacionAbierta && paso !== "final" && ventana && (
          <div style={S.aviso} role="status">
            {ahora < ventana.desdeMs
              ? t("modoSala.ventana.antes", { hora: horaMontevideo(ventana.desdeMs) })
              : t("modoSala.ventana.despues")}
          </div>
        )}
        {necesitaServidor && enHorario && errorEstado && (
          <div style={S.error} role="alert">
            {t("modoSala.errores.estado", { mensaje: errorEstado })}
          </div>
        )}
        {!mixta && otraAjena && (paso === "grabar" || paso === "sesion") && (
          <div style={S.aviso} role="status">
            {otraAjena.dispositivo
              ? t("modoSala.grabar.otroAjeno", { dispositivo: otraAjena.dispositivo })
              : t("modoSala.grabar.otroAjenoSinDato")}
          </div>
        )}
        {!mixta && !otraAjena && otrasGrabando.propias.length > 0 && (paso === "grabar" || paso === "sesion") && (
          <div style={S.aviso} role="status">
            {t("modoSala.grabar.otroDispositivo")}
          </div>
        )}

        {paso === "chequeo" && (
          <PasoChequeo
            t={t}
            mixta={mixta}
            mic={mic}
            nivel={nivel}
            grabando={grabacionAbierta}
            formatoOk={!!elegirFormato(deps.MediaRecorder)}
            wakeSoportado={wakeSoportado}
            wakeActivo={wakeActivo}
            bateria={bateria}
            onProbar={() => {
              if (wakeRef.current) wakeRef.current.pedir();
              abrirMicrofono(null).catch(() => {});
            }}
            onElegirEntrada={(id) => abrirMicrofono(id).catch(() => {})}
            onBuscarDeNuevo={async () => {
              const entradas = await listarEntradas(deps.mediaDevices);
              if (montado.current) setMic((m) => ({ ...m, entradas }));
              const sugerida = sugerirEntrada(entradas);
              if (sugerida && sugerida.deviceId !== mic.entradaId) abrirMicrofono(sugerida.deviceId).catch(() => {});
            }}
            onSiguiente={() => irA("checkin")}
          />
        )}

        {paso === "checkin" && (
          <PasoCheckin
            t={t}
            idioma={idioma}
            unidades={unidades}
            checkins={checkins}
            quorum={quorum}
            cargado={!!estadoSala}
            habilitado={enHorario || grabacionAbierta}
            busqueda={busqueda}
            onBuscar={setBusqueda}
            guardandoUnidad={guardandoUnidad}
            onMarcar={marcarUnidad}
            empezada={empezada}
            onSiguiente={() => irA(empezada ? "sesion" : "guion")}
          />
        )}

        {paso === "guion" && (
          <PasoGuion t={t} idioma={idioma} asamblea={asamblea} quorum={quorum} ahora={ahora} mixta={mixta} onSiguiente={() => irA(empezada ? "sesion" : "grabar")} />
        )}

        {paso === "grabar" && (
          <PasoGrabar
            t={t}
            idioma={idioma}
            mixta={mixta}
            mic={mic}
            nivel={nivel}
            wakeActivo={wakeActivo}
            bateria={bateria}
            quorum={quorum}
            sesionesPrevias={sesionesPrevias.length}
            habilitado={enHorario}
            iniciando={iniciando}
            onGrabar={empezarAGrabar}
            onComenzar={comenzarMixta}
          />
        )}

        {paso === "sesion" && (
          <PasoSesion
            t={t}
            mixta={mixta}
            transcurridoMs={transcurridoMs}
            nivel={nivel}
            grabando={grabando}
            interrumpida={interrumpida}
            iniciando={iniciando}
            estadoActual={estadoActual}
            subida={subida}
            huecos={huecos}
            wakeSoportado={wakeSoportado}
            wakeActivo={wakeActivo}
            puntos={puntos}
            puntoActual={puntoActual}
            resultados={resultados}
            marcasPendientes={marcasPendientes}
            errorMarcas={errorMarcas}
            onSiguiente={siguientePunto}
            onAnterior={puntoAnterior}
            onResultado={anotarResultado}
            onSeguir={seguirGrabando}
            onCheckin={() => irA("checkin")}
            onTerminar={() => setConfirmarTerminar(true)}
          />
        )}

        {paso === "final" && (
          <PasoFinal
            t={t}
            mixta={mixta}
            subida={subida}
            huecos={huecos}
            final={final}
            hayGrabacion={grabadoras.length > 0}
            procesando={procesando}
            procesado={procesado}
            onProcesar={() => procesar()}
            onDescargar={descargarCopia}
            onVolver={onSalir}
          />
        )}
      </main>

      <ModalRepresentada
        t={t}
        rep={rep}
        onCambiar={(campos) => setRep((r) => r && { ...r, ...campos })}
        onFoto={elegirFoto}
        onCerrar={cerrarRepresentada}
        onGuardar={guardarRepresentada}
      />

      <AdminModal
        open={confirmarTerminar}
        onClose={() => setConfirmarTerminar(false)}
        title={t("modoSala.terminar.titulo")}
        size="narrow"
        footer={
          <div style={S.pieModal}>
            <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={() => setConfirmarTerminar(false)}>
              {t("modoSala.terminar.seguir")}
            </button>
            <button type="button" style={{ ...btnDanger, ...S.botonModal }} className="habitia-btn-danger" onClick={terminar}>
              <FaStop /> {t("modoSala.terminar.confirmar")}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>{mixta ? t("modoSala.terminar.textoMixta") : t("modoSala.terminar.texto")}</p>
      </AdminModal>

      <AdminModal
        open={!!confirmarOtro}
        onClose={() => setConfirmarOtro(null)}
        title={t("modoSala.otroGrabando.titulo")}
        size="narrow"
        footer={
          <div style={S.pieModal}>
            <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={() => setConfirmarOtro(null)}>
              {t("modoSala.otroGrabando.volver")}
            </button>
            <button
              type="button"
              style={{ ...btnDanger, ...S.botonModal }}
              className="habitia-btn-danger"
              onClick={() => {
                const c = confirmarOtro;
                setConfirmarOtro(null);
                empezarAGrabar({ forzar: true, continuaDe: (c && c.continuaDe) || null });
              }}
            >
              <FaCircle /> {t("modoSala.otroGrabando.grabarIgual")}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>
          {confirmarOtro && confirmarOtro.dispositivo
            ? t("modoSala.otroGrabando.texto", { dispositivo: confirmarOtro.dispositivo })
            : t("modoSala.otroGrabando.textoSinDato")}
        </p>
      </AdminModal>

      <AdminModal
        open={confirmarSalida}
        onClose={() => setConfirmarSalida(false)}
        title={t("modoSala.salida.titulo")}
        size="narrow"
        footer={
          <div style={S.pieModal}>
            <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={() => setConfirmarSalida(false)}>
              {t("modoSala.salida.quedarme")}
            </button>
            <button type="button" style={{ ...btnDanger, ...S.botonModal }} className="habitia-btn-danger" onClick={salirIgual}>
              {t("modoSala.salida.salir")}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>{grabacionAbierta ? t("modoSala.salida.textoGrabando") : t("modoSala.salida.texto")}</p>
      </AdminModal>

      <AdminModal
        open={!!salaViva}
        onClose={() => setSalaViva(null)}
        title={t("modoSala.final.salaGrabandoTitulo")}
        size="narrow"
        footer={
          <div style={S.pieModal}>
            <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={() => setSalaViva(null)}>
              {t("modoSala.final.salaGrabandoNo")}
            </button>
            <button
              type="button"
              style={{ ...btnPrimary, ...S.botonModal }}
              className="habitia-btn-primary"
              onClick={() => procesar({ confirmarReproceso: !!(salaViva && salaViva.reproceso), confirmarSalaGrabando: true })}
            >
              {t("modoSala.final.salaGrabandoSi")}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>
          {t("modoSala.final.salaGrabandoTexto", {
            dispositivos: salaViva && salaViva.dispositivos.length ? salaViva.dispositivos.join(", ") : t("modoSala.final.salaGrabandoOtro"),
          })}
        </p>
      </AdminModal>

      <AdminModal
        open={!!confirmarReproceso}
        onClose={() => setConfirmarReproceso(null)}
        title={t("modoSala.final.reprocesoTitulo")}
        size="narrow"
        footer={
          <div style={S.pieModal}>
            <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={() => setConfirmarReproceso(null)}>
              {t("modoSala.final.reprocesoNo")}
            </button>
            <button
              type="button"
              style={{ ...btnPrimary, ...S.botonModal }}
              className="habitia-btn-primary"
              onClick={() => procesar({ confirmarReproceso: true, confirmarSalaGrabando: !!(confirmarReproceso && confirmarReproceso.salaGrabando) })}
            >
              {t("modoSala.final.reprocesoSi")}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>{t("modoSala.final.reprocesoTexto")}</p>
      </AdminModal>
    </div>
  );
}

// ─────────────────────────────── Piezas ───────────────────────────────

/** Barra de nivel del micrófono (−60 a 0 dB). */
function MedidorNivel({ t, nivel }) {
  const db = Number.isFinite(nivel) ? nivel : null;
  const pct = db == null ? 0 : Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
  const color = db == null ? colors.gray300 : db > -10 ? colors.warning : db > -50 ? colors.success : colors.gray400;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
      <div
        role="meter"
        aria-label={t("modoSala.nivel.aria")}
        aria-valuemin={-60}
        aria-valuemax={0}
        aria-valuenow={db == null ? -60 : Math.max(-60, db)}
        style={S.medidor}
      >
        <div style={{ ...S.medidorBarra, width: `${pct}%`, background: color }} />
      </div>
      <span style={S.hint}>{db == null ? t("modoSala.nivel.sinDato") : t("modoSala.nivel.valor", { db })}</span>
    </div>
  );
}

function FilaEstado({ ok, texto, detalle }) {
  return (
    <li style={S.filaEstado}>
      <span style={{ ...S.iconoEstado, color: ok ? colors.success : colors.warning }} aria-hidden="true">
        {ok ? <FaCheck /> : <FaExclamationTriangle />}
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: spacing[1] }}>
        <span style={S.texto}>{texto}</span>
        {detalle && <span style={S.hint}>{detalle}</span>}
      </span>
    </li>
  );
}

function PasoChequeo({ t, mixta, mic, nivel, grabando, formatoOk, wakeSoportado, wakeActivo, bateria, onProbar, onElegirEntrada, onBuscarDeNuevo, onSiguiente }) {
  const elegida = mic.entradas.find((e) => e.deviceId === mic.entradaId) || null;
  const externa = !!(elegida && elegida.externa);
  return (
    <>
      {mixta ? (
        <section style={S.card} aria-labelledby="sala-chequeo-mixta">
          <h2 id="sala-chequeo-mixta" style={S.h2}>
            <FaMicrophone color={colors.primary} aria-hidden="true" /> {t("modoSala.chequeo.mixtaTitulo")}
          </h2>
          <p style={S.texto}>{t("modoSala.chequeo.mixtaTexto")}</p>
        </section>
      ) : (
        <section style={S.card} aria-labelledby="sala-chequeo-mic">
          <h2 id="sala-chequeo-mic" style={S.h2}>
            <FaMicrophone color={colors.primary} aria-hidden="true" /> {t("modoSala.chequeo.micTitulo")}
          </h2>
          {!formatoOk && (
            <div style={S.error} role="alert">
              {t("modoSala.mic.error.formato")}
            </div>
          )}
          {mic.estado === "sin_probar" && <p style={S.texto}>{t("modoSala.chequeo.micTexto")}</p>}
          {mic.estado === "pidiendo" && <p style={S.texto}>{t("modoSala.chequeo.pidiendo")}</p>}
          {mic.estado === "error" && (
            <div style={S.error} role="alert">
              {t(`modoSala.mic.error.${mic.motivo || "otro"}`)}
            </div>
          )}
          {mic.estado === "listo" && (
            <>
              <FormField label={t("modoSala.chequeo.entrada")} htmlFor="sala-entrada" hint={externa ? t("modoSala.chequeo.externaOk") : t("modoSala.chequeo.externaNo")}>
                <select
                  id="sala-entrada"
                  className="habitia-input"
                  style={{ ...inputBase, ...S.inputGrande }}
                  value={mic.entradaId || ""}
                  disabled={grabando}
                  onChange={(e) => onElegirEntrada(e.target.value)}
                >
                  {!mic.entradas.length && <option value="">{mic.etiqueta || t("modoSala.chequeo.entradaSistema")}</option>}
                  {mic.entradas.map((e, i) => (
                    <option key={e.deviceId} value={e.deviceId}>
                      {e.label || t("modoSala.chequeo.entradaN", { n: i + 1 })}
                      {e.externa ? ` · ${t("modoSala.chequeo.externa")}` : ""}
                    </option>
                  ))}
                </select>
              </FormField>
              <MedidorNivel t={t} nivel={nivel} />
              <p style={S.hint}>{t("modoSala.chequeo.nivelAyuda")}</p>
            </>
          )}
          <div style={S.filaBotones}>
            {mic.estado !== "listo" ? (
              <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onProbar} disabled={mic.estado === "pidiendo" || !formatoOk}>
                <FaMicrophone /> {mic.estado === "error" ? t("modoSala.chequeo.probarDeNuevo") : t("modoSala.chequeo.probar")}
              </button>
            ) : (
              <button type="button" style={{ ...btnSecondary, ...S.botonMediano }} className="habitia-btn-secondary" onClick={onBuscarDeNuevo} disabled={grabando}>
                <FaSync /> {t("modoSala.chequeo.buscarDeNuevo")}
              </button>
            )}
          </div>
        </section>
      )}

      <section style={S.card} aria-labelledby="sala-chequeo-equipo">
        <h2 id="sala-chequeo-equipo" style={S.h2}>
          <FaMobileAlt color={colors.primary} aria-hidden="true" /> {t("modoSala.chequeo.equipoTitulo")}
        </h2>
        <ul style={S.listaEstado}>
          <FilaEstado
            ok={wakeActivo}
            texto={wakeActivo ? t("modoSala.chequeo.pantallaOk") : wakeSoportado ? t("modoSala.chequeo.pantallaPendiente") : t("modoSala.chequeo.pantallaNo")}
          />
          <FilaEstado
            ok={!!(bateria && bateria.cargando)}
            texto={bateria && bateria.cargando ? t("modoSala.chequeo.cargadorOk") : t("modoSala.chequeo.cargador")}
            detalle={bateria && Number.isFinite(bateria.nivel) ? t("modoSala.chequeo.bateria", { pct: Math.round(bateria.nivel * 100) }) : null}
          />
        </ul>
        {!mixta && <p style={S.hint}>{t("modoSala.chequeo.externo")}</p>}
      </section>

      <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onSiguiente}>
        {t("modoSala.siguienteCheckin")}
      </button>
    </>
  );
}

/**
 * Badge del quórum: "Hay quórum", "Todavía sin quórum" o, en una mixta con
 * link manual (el servidor no ve a los conectados), "Solo la sala": ahí no se
 * puede decir que no alcanza.
 */
function estadoQuorum(t, quorum) {
  if (quorum.alcanzado) return { status: "presente", label: t("modoSala.quorum.si") };
  if (quorum.soloSala) return { status: "en_proceso", label: t("modoSala.quorum.soloSala") };
  return { status: "pendiente", label: t("modoSala.quorum.no") };
}

function ResumenQuorum({ t, idioma, quorum }) {
  if (!quorum) return null;
  const pct = new Intl.NumberFormat(idioma, { maximumFractionDigits: 1 }).format(quorum.valorPct);
  const umbral = quorum.umbral != null ? new Intl.NumberFormat(idioma, { maximumFractionDigits: 1 }).format(quorum.umbral) : null;
  const badge = estadoQuorum(t, quorum);
  return (
    <section style={{ ...S.card, gap: spacing[3] }} aria-label={t("modoSala.quorum.aria")} aria-live="polite">
      <div style={S.filaEntre}>
        <h2 style={S.h2}>
          <FaUsers color={colors.primary} aria-hidden="true" /> {t("modoSala.quorum.titulo")}
        </h2>
        <StatusBadge status={badge.status} label={badge.label} />
      </div>
      <div style={S.kpis}>
        <div style={S.kpi}>
          <span style={S.kpiValor}>{t("modoSala.quorum.cabezasValor", { presentes: quorum.presentes, total: quorum.total })}</span>
          <span style={S.hint}>
            {quorum.remotas > 0 ? t("modoSala.quorum.cabezasRemotas", { count: quorum.remotas }) : t("modoSala.quorum.cabezas")}
          </span>
        </div>
        <div style={S.kpi}>
          <span style={S.kpiValor}>{t("modoSala.quorum.valorValor", { pct })}</span>
          <span style={S.hint}>{umbral != null ? t("modoSala.quorum.valorUmbral", { umbral }) : t("modoSala.quorum.valor")}</span>
        </div>
      </div>
      {quorum.soloSala && <p style={S.hint}>{t("modoSala.quorum.soloSalaAyuda")}</p>}
    </section>
  );
}

function PasoCheckin({ t, idioma, unidades, checkins, quorum, cargado, habilitado, busqueda, onBuscar, guardandoUnidad, onMarcar, empezada, onSiguiente }) {
  const filtro = sinAcentos(busqueda.trim());
  const visibles = filtro ? unidades.filter((u) => sinAcentos([u.etiqueta, ...u.titulares].join(" ")).includes(filtro)) : unidades;
  const sinRegistrar = unidades.filter((u) => !checkins[u.id]).length;
  return (
    <>
      <ResumenQuorum t={t} idioma={idioma} quorum={quorum} />
      <section style={S.card} aria-labelledby="sala-checkin">
        <div style={S.filaEntre}>
          <h2 id="sala-checkin" style={S.h2}>
            {t("modoSala.checkin.titulo")}
          </h2>
          {unidades.length > 0 && <span style={S.hint}>{t("modoSala.checkin.sinRegistrar", { n: sinRegistrar })}</span>}
        </div>
        <p style={S.hint}>{t("modoSala.checkin.ayuda")}</p>
        <div style={S.buscador}>
          <FaSearch color={colors.gray400} aria-hidden="true" />
          <label htmlFor="sala-buscar" style={S.oculto}>
            {t("modoSala.checkin.buscar")}
          </label>
          <input
            id="sala-buscar"
            type="search"
            className="habitia-input"
            style={{ ...inputBase, ...S.inputGrande, border: "none", boxShadow: "none", padding: 0 }}
            placeholder={t("modoSala.checkin.buscar")}
            value={busqueda}
            onChange={(e) => onBuscar(e.target.value)}
          />
        </div>
        {!cargado ? (
          <p style={S.texto}>{habilitado ? t("modoSala.cargando") : t("modoSala.checkin.fueraDeHorario")}</p>
        ) : unidades.length === 0 ? (
          <EmptyState icon={FaUsers} title={t("modoSala.checkin.vacioTitulo")} description={t("modoSala.checkin.vacioTexto")} />
        ) : (
          <ul style={S.listaUnidades} aria-label={t("modoSala.checkin.lista")}>
            {visibles.map((u) => {
              const c = checkins[u.id] || null;
              const guardando = guardandoUnidad === u.id;
              return (
                <li key={u.id} style={S.unidad} aria-label={u.etiqueta}>
                  <div style={S.filaEntre}>
                    <span style={S.unidadEtiqueta}>{u.etiqueta}</span>
                    <StatusBadge
                      status={c ? BADGE_CHECKIN[c.estado] : "libre"}
                      label={c ? t(`modoSala.checkin.estado.${c.estado}`) : t("modoSala.checkin.estado.sin")}
                      size="sm"
                    />
                  </div>
                  {u.titulares.length > 0 && <span style={S.hint}>{u.titulares.join(" · ")}</span>}
                  {c && c.estado === "representada" && c.representante && c.representante.nombre && (
                    <span style={S.hint}>
                      {t("modoSala.checkin.representadaPor", { nombre: c.representante.nombre })}
                      {!c.conPoder ? ` · ${t("modoSala.checkin.sinFoto")}` : ""}
                    </span>
                  )}
                  <div style={S.botonesCheckin} role="group" aria-label={t("modoSala.checkin.grupo", { unidad: u.etiqueta })}>
                    {ESTADOS_CHECKIN.map((estado) => {
                      const elegido = !!c && c.estado === estado;
                      return (
                        <button
                          key={estado}
                          type="button"
                          aria-pressed={elegido}
                          disabled={guardando || !habilitado}
                          onClick={() => onMarcar(u, estado)}
                          style={{ ...(elegido ? btnPrimary : btnSecondary), ...S.botonCheckin }}
                          className={elegido ? "habitia-btn-primary" : "habitia-btn-secondary"}
                        >
                          {t(`modoSala.checkin.boton.${estado}`)}
                        </button>
                      );
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onSiguiente}>
        {empezada ? t("modoSala.volverSesion") : t("modoSala.siguienteGuion")}
      </button>
    </>
  );
}

/**
 * Párrafos del guion de apertura (con el aviso de grabación). En una mixta el
 * número de unidades incluye a las conectadas por Zoom (y lo dice); con link
 * manual (soloSala) habla solo de la sala y no dice que no alcanza el quórum.
 */
export function parrafosGuion({ t, idioma, asamblea, quorum, ahora, mixta }) {
  const hora = horaMontevideo(ahora);
  const h = Number(hora.slice(0, 2));
  const saludo = h < 12 ? "dias" : h < 20 ? "tardes" : "noches";
  const edificio = (asamblea.edificio && asamblea.edificio.nombre) || t("modoSala.guion.elEdificio");
  const tipo = t(`modoSala.guion.tipo.${asamblea.tipo === "extraordinaria" ? "extraordinaria" : "ordinaria"}`);
  const out = [t(`modoSala.guion.saludo.${saludo}`)];
  out.push(
    asamblea.lugar
      ? t("modoSala.guion.aperturaLugar", { hora, tipo, edificio, lugar: asamblea.lugar })
      : t("modoSala.guion.apertura", { hora, tipo, edificio })
  );
  if (quorum) {
    const valor = new Intl.NumberFormat(idioma, { maximumFractionDigits: 1 }).format(quorum.valorPct);
    const minutosSegunda = Number(asamblea.minutosSegunda) || 0;
    const segundaMs = Number(asamblea.inicioMs) + minutosSegunda * 60000;
    // Conectadas por Zoom (ya están dentro de presentes): el guion las nombra.
    const remotas = !quorum.soloSala && quorum.remotas > 0 ? quorum.remotas : 0;
    const base = { cabezas: quorum.presentes, valor, ...(remotas ? { count: remotas } : {}) };
    const clave = (nombre) => `modoSala.guion.${nombre}${remotas ? "Remotas" : ""}`;
    if (quorum.alcanzado) {
      const convocatoria = minutosSegunda > 0 && ahora >= segundaMs ? t("modoSala.guion.segunda") : t("modoSala.guion.primera");
      out.push(t(quorum.soloSala ? "modoSala.guion.quorumSiSala" : clave("quorumSi"), { ...base, convocatoria }));
    } else if (quorum.soloSala) {
      // Mixta con link manual: no se ve a los conectados, así que no se puede decir que no alcanza.
      out.push(t("modoSala.guion.quorumSoloSala", base));
    } else {
      out.push(
        minutosSegunda > 0 && ahora < segundaMs
          ? t(clave("quorumNo"), { ...base, hora: horaMontevideo(segundaMs) })
          : t(clave("quorumNoSegunda"), base)
      );
    }
  }
  out.push(mixta ? t("modoSala.guion.grabacionMixta") : t("modoSala.guion.grabacion"));
  out.push(t("modoSala.guion.reglas"));
  return out;
}

function PasoGuion({ t, idioma, asamblea, quorum, ahora, mixta, onSiguiente }) {
  const parrafos = parrafosGuion({ t, idioma, asamblea, quorum, ahora, mixta });
  const puntos = asamblea.ordenDelDia || [];
  return (
    <>
      <section style={S.card} aria-labelledby="sala-guion">
        <h2 id="sala-guion" style={S.h2}>
          {t("modoSala.guion.titulo")}
        </h2>
        <p style={S.hint}>{t("modoSala.guion.ayuda")}</p>
        <div style={S.guion}>
          {parrafos.map((p) => (
            <p key={p} style={S.guionParrafo}>
              {p}
            </p>
          ))}
          {puntos.length > 0 && (
            <>
              <p style={S.guionParrafo}>{t("modoSala.guion.ordenDelDia")}</p>
              <ol style={{ ...S.guionParrafo, paddingLeft: spacing[6], margin: 0 }}>
                {puntos.map((p) => (
                  <li key={p.id} style={{ marginBottom: spacing[2] }}>
                    {p.titulo}
                  </li>
                ))}
              </ol>
              <p style={S.guionParrafo}>{t("modoSala.guion.primerPunto", { titulo: puntos[0].titulo })}</p>
            </>
          )}
        </div>
      </section>
      <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onSiguiente}>
        {t("modoSala.siguienteGrabar")}
      </button>
    </>
  );
}

function PasoGrabar({ t, idioma, mixta, mic, nivel, wakeActivo, bateria, quorum, sesionesPrevias, habilitado, iniciando, onGrabar, onComenzar }) {
  if (mixta) {
    return (
      <>
        <section style={S.card} aria-labelledby="sala-grabar-mixta">
          <h2 id="sala-grabar-mixta" style={S.h2}>
            {t("modoSala.grabar.mixtaTitulo")}
          </h2>
          <p style={S.texto}>{t("modoSala.grabar.mixtaTexto")}</p>
          <ResumenQuorumCorto t={t} idioma={idioma} quorum={quorum} />
        </section>
        <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onComenzar} disabled={!habilitado}>
          <FaPlay /> {t("modoSala.grabar.comenzar")}
        </button>
      </>
    );
  }
  return (
    <>
      <section style={S.card} aria-labelledby="sala-grabar">
        <h2 id="sala-grabar" style={S.h2}>
          {t("modoSala.grabar.titulo")}
        </h2>
        <ul style={S.listaEstado}>
          <FilaEstado
            ok={mic.estado === "listo"}
            texto={mic.estado === "listo" ? t("modoSala.grabar.micOk", { entrada: mic.etiqueta || t("modoSala.chequeo.entradaSistema") }) : t("modoSala.grabar.micNo")}
          />
          <FilaEstado ok={wakeActivo} texto={wakeActivo ? t("modoSala.chequeo.pantallaOk") : t("modoSala.grabar.pantallaNo")} />
          <FilaEstado ok={!!(bateria && bateria.cargando)} texto={bateria && bateria.cargando ? t("modoSala.chequeo.cargadorOk") : t("modoSala.chequeo.cargador")} />
          {quorum && <FilaEstado ok={quorum.alcanzado} texto={estadoQuorum(t, quorum).label} />}
        </ul>
        {mic.estado === "listo" && <MedidorNivel t={t} nivel={nivel} />}
        {sesionesPrevias > 0 && (
          <div style={S.info} role="status">
            {t("modoSala.grabar.sesionesPrevias", { count: sesionesPrevias })}
          </div>
        )}
        <p style={S.hint}>{t("modoSala.grabar.ayuda")}</p>
      </section>
      <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onGrabar} disabled={iniciando || !habilitado}>
        <FaCircle color={colors.white} /> {iniciando ? t("modoSala.grabar.iniciando") : t("modoSala.grabar.empezar")}
      </button>
    </>
  );
}

function ResumenQuorumCorto({ t, idioma, quorum }) {
  if (!quorum) return null;
  const pct = new Intl.NumberFormat(idioma, { maximumFractionDigits: 1 }).format(quorum.valorPct);
  const datos = { presentes: quorum.presentes, total: quorum.total, pct };
  let detalle = t("modoSala.quorum.corto", datos);
  if (quorum.remotas > 0) detalle = t("modoSala.quorum.cortoRemotas", { ...datos, count: quorum.remotas });
  else if (quorum.soloSala) detalle = t("modoSala.quorum.cortoSoloSala", datos);
  return (
    <ul style={S.listaEstado}>
      <FilaEstado ok={quorum.alcanzado} texto={estadoQuorum(t, quorum).label} detalle={detalle} />
    </ul>
  );
}

function PasoSesion({
  t,
  mixta,
  transcurridoMs,
  nivel,
  grabando,
  interrumpida,
  iniciando,
  estadoActual,
  subida,
  huecos,
  wakeSoportado,
  wakeActivo,
  puntos,
  puntoActual,
  resultados,
  marcasPendientes,
  errorMarcas,
  onSiguiente,
  onAnterior,
  onResultado,
  onSeguir,
  onCheckin,
  onTerminar,
}) {
  const punto = puntoActual >= 0 ? puntos[puntoActual] : null;
  const hayMas = puntoActual + 1 < puntos.length;
  const resultado = punto ? resultados[String(punto.id)] || null : null;
  const microfono = estadoActual ? estadoActual.microfono : "ok";
  // Ratos sin audio (la captura se pausó): los más nuevos primero.
  const ultimosHuecos = (huecos || []).slice(-HUECOS_A_LA_VISTA).reverse();
  const huecosOcultos = Math.max(0, (huecos || []).length - ultimosHuecos.length);
  const rango = (h) => ({ desde: formatoDuracion(h.desdeMs), hasta: formatoDuracion(h.hastaMs) });
  return (
    <>
      <section style={{ ...S.card, ...S.cardSesion }} aria-label={t("modoSala.sesion.aria")}>
        <div style={S.filaEntre}>
          <span style={S.estadoSesion}>
            {!mixta && grabando && <FaCircle color={colors.error} aria-hidden="true" />}
            {mixta ? t("modoSala.sesion.enCurso") : grabando ? t("modoSala.sesion.grabando") : t("modoSala.sesion.sinGrabar")}
          </span>
          <span style={S.reloj} aria-label={t("modoSala.sesion.transcurrido")}>
            {formatoDuracion(transcurridoMs)}
          </span>
        </div>
        {!mixta && grabando && <MedidorNivel t={t} nivel={nivel} />}
        {!mixta && (
          <div style={S.filaSubida} aria-live="polite">
            <FaCloudUploadAlt color={colors.primary} aria-hidden="true" />
            <span style={S.texto}>{t("modoSala.sesion.subidas", { confirmados: subida.confirmados, objetos: subida.objetos })}</span>
            {subida.pendientes > 0 && <StatusBadge status="pendiente" label={t("modoSala.sesion.enCola", { count: subida.pendientes })} size="sm" />}
          </div>
        )}
      </section>

      {!mixta && interrumpida && (
        <div style={S.errorConAcciones} role="alert">
          <span>{t("modoSala.sesion.interrumpida")}</span>
          <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onSeguir} disabled={iniciando}>
            <FaRedo /> {iniciando ? t("modoSala.grabar.iniciando") : t("modoSala.sesion.seguir")}
          </button>
        </div>
      )}
      {!mixta && grabando && microfono === "silenciado" && (
        <div style={S.aviso} role="alert">
          {t("modoSala.sesion.microfonoPausa")}
        </div>
      )}
      {!mixta && ultimosHuecos.length > 0 && (
        <div style={S.avisoLista} role="alert" aria-label={t("modoSala.sesion.huecosAria")}>
          {ultimosHuecos.length === 1 && !huecosOcultos ? (
            <span>{t("modoSala.sesion.hueco", rango(ultimosHuecos[0]))}</span>
          ) : (
            <>
              <span>{t("modoSala.sesion.huecosVarios")}</span>
              <ul style={S.listaHuecos}>
                {ultimosHuecos.map((h) => (
                  <li key={`${h.desdeMs}-${h.hastaMs}`}>{t("modoSala.sesion.huecoRango", rango(h))}</li>
                ))}
              </ul>
              {huecosOcultos > 0 && <span>{t("modoSala.sesion.huecosMas", { count: huecosOcultos })}</span>}
            </>
          )}
        </div>
      )}
      {!mixta && subida.sesionRechazada && (
        <div style={S.error} role="alert">
          {t("modoSala.sesion.rechazada")}
        </div>
      )}
      {!mixta && !subida.sesionRechazada && subida.conError && (
        <div style={S.error} role="alert">
          {t("modoSala.sesion.trabada")}
        </div>
      )}
      {!mixta && !subida.sesionRechazada && !subida.conError && subida.sinConexion && (
        <div style={S.aviso} role="status">
          {t("modoSala.sesion.sinConexion")}
        </div>
      )}
      {grabando && !wakeActivo && (
        <div style={S.aviso} role="status">
          {wakeSoportado ? t("modoSala.sesion.pantallaPendiente") : t("modoSala.sesion.pantallaNo")}
        </div>
      )}

      <section style={S.card} aria-labelledby="sala-punto">
        <span id="sala-punto" style={S.hint}>
          {punto ? t("modoSala.sesion.puntoN", { n: puntoActual + 1, total: puntos.length }) : t("modoSala.sesion.apertura")}
        </span>
        <span style={S.puntoTitulo}>{punto ? punto.titulo : t("modoSala.sesion.aperturaTexto")}</span>
        {punto && (
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
            <span style={S.hint}>{t("modoSala.sesion.resultadoAyuda")}</span>
            <div style={S.botonesResultado} role="group" aria-label={t("modoSala.sesion.resultado")}>
              {RESULTADOS.map((r) => {
                const elegido = resultado === r;
                return (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={elegido}
                    onClick={() => onResultado(r)}
                    style={{ ...(elegido ? btnPrimary : btnSecondary), ...S.botonCheckin }}
                    className={elegido ? "habitia-btn-primary" : "habitia-btn-secondary"}
                  >
                    {t(`modoSala.resultado.${r}`)}
                  </button>
                );
              })}
            </div>
            {resultado && <StatusBadge status={BADGE_RESULTADO[resultado]} label={t("modoSala.sesion.anotado", { resultado: t(`modoSala.resultado.${resultado}`) })} size="sm" />}
          </div>
        )}
        <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onSiguiente} disabled={!hayMas}>
          <FaStepForward />{" "}
          {!hayMas
            ? t("modoSala.sesion.ultimoPunto")
            : puntoActual < 0
              ? t("modoSala.sesion.empezarPunto1", { titulo: puntos[0] ? puntos[0].titulo : "" })
              : t("modoSala.sesion.siguientePunto", { n: puntoActual + 2 })}
        </button>
        {puntoActual > 0 && (
          <button type="button" style={{ ...btnSecondary, ...S.botonMediano }} className="habitia-btn-secondary" onClick={onAnterior}>
            <FaStepBackward /> {t("modoSala.sesion.puntoAnterior")}
          </button>
        )}
        {marcasPendientes > 0 && <span style={S.hint}>{t("modoSala.sesion.marcasPendientes", { count: marcasPendientes })}</span>}
        {errorMarcas && marcasPendientes === 0 && (
          <span style={{ ...S.hint, color: colors.errorDark }} role="status">
            {t("modoSala.sesion.errorMarca", { mensaje: errorMarcas })}
          </span>
        )}
      </section>

      <div style={S.filaBotones}>
        <button type="button" style={{ ...btnSecondary, ...S.botonMediano, flex: 1 }} className="habitia-btn-secondary" onClick={onCheckin}>
          <FaUsers /> {t("modoSala.sesion.checkin")}
        </button>
        <button type="button" style={{ ...btnDanger, ...S.botonMediano, flex: 1 }} className="habitia-btn-danger" onClick={onTerminar}>
          <FaStop /> {t("modoSala.sesion.terminar")}
        </button>
      </div>
    </>
  );
}

function PasoFinal({ t, mixta, subida, huecos, final, hayGrabacion, procesando, procesado, onProcesar, onDescargar, onVolver }) {
  if (mixta) {
    return (
      <>
        <section style={S.card} aria-labelledby="sala-final">
          <h2 id="sala-final" style={S.h2}>
            <FaCheck color={colors.success} aria-hidden="true" /> {t("modoSala.final.mixtaTitulo")}
          </h2>
          <p style={S.texto}>{t("modoSala.final.mixtaTexto")}</p>
        </section>
        <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onVolver}>
          {t("modoSala.volverPanel")}
        </button>
      </>
    );
  }
  const listo = !!(final && final.listo);
  const completas = final && final.completas ? final.completas : 0;
  const errores = (final && final.errores) || [];
  return (
    <>
      <section style={S.card} aria-labelledby="sala-final">
        <h2 id="sala-final" style={S.h2}>
          <FaCloudUploadAlt color={colors.primary} aria-hidden="true" /> {t("modoSala.final.titulo")}
        </h2>
        {final && final.sinGrabacion ? (
          <p style={S.texto}>{t("modoSala.final.sinGrabacion")}</p>
        ) : (
          <>
            <div
              role="progressbar"
              aria-label={t("modoSala.final.progreso")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={subida.porcentaje}
              style={S.medidor}
            >
              <div style={{ ...S.medidorBarra, width: `${subida.porcentaje}%`, background: subida.todoSubido ? colors.success : colors.primary }} />
            </div>
            <span style={S.porcentaje}>
              {subida.todoSubido ? t("modoSala.final.cienPorCiento") : t("modoSala.final.subiendo", { pct: subida.porcentaje })}
            </span>
            {!subida.todoSubido && <p style={S.texto}>{t("modoSala.final.noCierres")}</p>}
            {!subida.todoSubido && subida.sinConexion && (
              <div style={S.aviso} role="status">
                {t("modoSala.final.sinConexion")}
              </div>
            )}
            {listo && completas > 0 && (
              <div style={S.ok} role="status">
                {t("modoSala.final.completa")}
              </div>
            )}
            {listo && completas === 0 && (
              <div style={S.error} role="alert">
                {t("modoSala.final.incompleta")}
                {errores.length > 0 ? ` (${errores[0]})` : ""}
              </div>
            )}
            {listo && completas > 0 && errores.length > 0 && (
              <div style={S.aviso} role="status">
                {t("modoSala.final.parcial", { mensaje: errores[0] })}
              </div>
            )}
            {(huecos || []).length > 0 && (
              <div style={S.aviso} role="status">
                {t("modoSala.final.huecos", { count: huecos.length })}
              </div>
            )}
          </>
        )}
      </section>

      {listo && completas > 0 && (
        <section style={S.card} aria-labelledby="sala-procesar">
          <h2 id="sala-procesar" style={S.h2}>
            <FaFileContract color={colors.primary} aria-hidden="true" /> {t("modoSala.final.actaTitulo")}
          </h2>
          {procesado ? (
            procesado.aviso ? (
              <div style={S.aviso} role="status">
                {procesado.aviso}
              </div>
            ) : (
              <div style={procesado.workerHabilitado === false ? S.aviso : S.ok} role="status">
                {procesado.workerHabilitado === false ? t("modoSala.final.enColaSinWorker") : t("modoSala.final.enCola")}
              </div>
            )
          ) : (
            <>
              <p style={S.texto}>{t("modoSala.final.actaTexto")}</p>
              <button type="button" style={{ ...btnPrimary, ...S.botonGrande }} className="habitia-btn-primary" onClick={onProcesar} disabled={procesando}>
                <FaFileContract /> {procesando ? t("modoSala.final.procesando") : t("modoSala.final.procesar")}
              </button>
            </>
          )}
        </section>
      )}

      {hayGrabacion && (
        <section style={S.card} aria-labelledby="sala-copia">
          <h2 id="sala-copia" style={S.h2}>
            <FaDownload color={colors.primary} aria-hidden="true" /> {t("modoSala.final.copiaTitulo")}
          </h2>
          <p style={S.hint}>{t("modoSala.final.copiaTexto")}</p>
          <button type="button" style={{ ...btnSecondary, ...S.botonMediano }} className="habitia-btn-secondary" onClick={onDescargar}>
            <FaDownload /> {t("modoSala.final.descargar")}
          </button>
        </section>
      )}

      <p style={S.hint}>{t("modoSala.final.externa")}</p>
      <button type="button" style={{ ...btnSecondary, ...S.botonGrande }} className="habitia-btn-secondary" onClick={onVolver} disabled={!subida.todoSubido && hayGrabacion && !listo}>
        {t("modoSala.volverPanel")}
      </button>
    </>
  );
}

function ModalRepresentada({ t, rep, onCambiar, onFoto, onCerrar, onGuardar }) {
  const abierto = !!rep;
  return (
    <AdminModal
      open={abierto}
      onClose={() => {
        if (!rep || !rep.guardando) onCerrar();
      }}
      title={rep ? t("modoSala.checkin.repTitulo", { unidad: rep.unidad.etiqueta }) : ""}
      size="narrow"
      footer={
        <div style={S.pieModal}>
          <button type="button" style={{ ...btnSecondary, ...S.botonModal }} className="habitia-btn-secondary" onClick={onCerrar} disabled={!!(rep && rep.guardando)}>
            {t("modoSala.checkin.cancelar")}
          </button>
          <button type="button" style={{ ...btnPrimary, ...S.botonModal }} className="habitia-btn-primary" onClick={onGuardar} disabled={!!(rep && rep.guardando)}>
            {rep && rep.guardando
              ? rep.progreso != null
                ? t("modoSala.checkin.subiendoFoto", { pct: rep.progreso })
                : t("modoSala.checkin.guardando")
              : t("modoSala.checkin.guardar")}
          </button>
        </div>
      }
    >
      {rep && (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }}>
          <FormField label={t("modoSala.checkin.repNombre")} htmlFor="sala-rep-nombre" required error={rep.error && !rep.nombre.trim() ? rep.error : undefined}>
            <input
              id="sala-rep-nombre"
              type="text"
              autoComplete="off"
              maxLength={120}
              className="habitia-input"
              style={{ ...inputBase, ...S.inputGrande }}
              value={rep.nombre}
              disabled={rep.guardando}
              onChange={(e) => onCambiar({ nombre: e.target.value, error: "" })}
            />
          </FormField>
          <FormField label={t("modoSala.checkin.repDocumento")} htmlFor="sala-rep-doc">
            <input
              id="sala-rep-doc"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={30}
              className="habitia-input"
              style={{ ...inputBase, ...S.inputGrande }}
              value={rep.documento}
              disabled={rep.guardando}
              onChange={(e) => onCambiar({ documento: e.target.value })}
            />
          </FormField>
          <FormField label={t("modoSala.checkin.foto")} htmlFor="sala-rep-foto" hint={t("modoSala.checkin.fotoAyuda")}>
            <input
              id="sala-rep-foto"
              type="file"
              accept="image/*"
              capture="environment"
              disabled={rep.guardando}
              style={S.inputArchivo}
              onChange={(e) => onFoto(e.target.files && e.target.files[0])}
            />
          </FormField>
          {rep.archivo && (
            <div style={S.vistaPoder}>
              {rep.vista ? <img src={rep.vista} alt={t("modoSala.checkin.fotoAlt")} style={S.vistaImagen} /> : <FaCamera color={colors.gray400} aria-hidden="true" />}
              <span style={S.hint}>{rep.archivo.name || t("modoSala.checkin.fotoLista")}</span>
            </div>
          )}
          {rep.error && rep.nombre.trim() && (
            <div style={S.error} role="alert">
              {rep.error}
            </div>
          )}
        </div>
      )}
    </AdminModal>
  );
}

// ─────────────────────────────── Estilos ───────────────────────────────
// Mobile-first: una columna de hasta 720 px, botones de 48–56 px de alto y
// texto de 14–16 px en gris oscuro sobre blanco (se usa de pie, con luz de sala).

const S = {
  pagina: {
    minHeight: "100vh",
    background: colors.bgMain,
    fontFamily: typography.fontFamily,
    color: colors.gray900,
    display: "flex",
    flexDirection: "column",
  },
  barra: {
    position: "sticky",
    top: 0,
    zIndex: zIndex.sticky,
    background: colors.white,
    boxShadow: shadows.sm,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
  },
  barraTexto: { display: "flex", flexDirection: "column", gap: spacing[1], flex: 1, minWidth: 0 },
  titulo: { margin: 0, fontSize: typography.sizes.xl, fontWeight: 400, color: colors.primary },
  subtitulo: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  botonIcono: { width: 48, height: 48, color: colors.gray900 },
  pasos: {
    display: "flex",
    gap: spacing[2],
    overflowX: "auto",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.white,
    borderTop: `1px solid ${colors.gray100}`,
  },
  paso: {
    flex: "0 0 auto",
    minHeight: 48,
    padding: `0 ${spacing[4]}px`,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.gray200,
    background: colors.white,
    color: colors.gray900,
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  pasoActivo: { background: colors.primaryDark, borderColor: colors.primaryDark, color: colors.white },
  pasoApagado: { color: colors.gray400, cursor: "default" },
  contenido: {
    width: "100%",
    maxWidth: 720,
    margin: "0 auto",
    padding: spacing[4],
    display: "flex",
    flexDirection: "column",
    gap: spacing[4],
    flex: 1,
    boxSizing: "border-box",
  },
  card: { ...cardBase, display: "flex", flexDirection: "column", gap: spacing[3] },
  cardSesion: { gap: spacing[4] },
  h2: { margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.black, display: "flex", alignItems: "center", gap: spacing[2] },
  texto: { margin: 0, fontSize: typography.sizes.lg, color: colors.gray900, lineHeight: 1.5 },
  hint: { margin: 0, fontSize: typography.sizes.md, color: colors.gray600, lineHeight: 1.5 },
  oculto: { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" },
  filaEntre: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing[3], flexWrap: "wrap" },
  filaBotones: { display: "flex", gap: spacing[3], flexWrap: "wrap" },
  botonGrande: { minHeight: 56, width: "100%", justifyContent: "center", fontSize: typography.sizes.lg, boxSizing: "border-box" },
  botonMediano: { minHeight: 48, justifyContent: "center", fontSize: typography.sizes.md },
  botonModal: { minHeight: 48 },
  botonCheckin: { minHeight: 48, flex: "1 1 0", justifyContent: "center", padding: `0 ${spacing[3]}px`, fontSize: typography.sizes.md },
  pieModal: { display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%", flexWrap: "wrap" },
  inputGrande: { minHeight: 48, fontSize: typography.sizes.lg, boxSizing: "border-box" },
  inputArchivo: { fontSize: typography.sizes.md, fontFamily: typography.fontFamily, minHeight: 48 },
  medidor: { width: "100%", height: 16, borderRadius: radius.full, background: colors.gray100, overflow: "hidden" },
  medidorBarra: { height: "100%", borderRadius: radius.full, transition: `width ${transitions.fast}` },
  listaEstado: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[3] },
  filaEstado: { display: "flex", alignItems: "flex-start", gap: spacing[3] },
  iconoEstado: { display: "inline-flex", paddingTop: spacing[1], fontSize: typography.sizes.lg },
  buscador: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: `0 ${spacing[3]}px`,
    background: colors.white,
  },
  listaUnidades: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[3] },
  unidad: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
    padding: spacing[3],
    borderRadius: radius.sm,
    background: colors.gray50,
  },
  unidadEtiqueta: { fontSize: typography.sizes.xl, color: colors.black },
  botonesCheckin: { display: "flex", gap: spacing[2] },
  botonesResultado: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: spacing[2] },
  kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: spacing[3] },
  kpi: { display: "flex", flexDirection: "column", gap: spacing[1], background: colors.gray50, borderRadius: radius.sm, padding: spacing[3] },
  kpiValor: { fontSize: typography.sizes["3xl"], color: colors.black, lineHeight: 1.2 },
  guion: { display: "flex", flexDirection: "column", gap: spacing[3] },
  guionParrafo: { margin: 0, fontSize: typography.sizes.xl, color: colors.black, lineHeight: 1.6 },
  estadoSesion: { display: "inline-flex", alignItems: "center", gap: spacing[2], fontSize: typography.sizes.lg, color: colors.black },
  reloj: { fontSize: typography.sizes["3xl"], color: colors.black, fontVariantNumeric: "tabular-nums" },
  filaSubida: { display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" },
  puntoTitulo: { fontSize: typography.sizes["2xl"], color: colors.black, lineHeight: 1.3 },
  porcentaje: { fontSize: typography.sizes["2xl"], color: colors.black },
  vistaPoder: { display: "flex", alignItems: "center", gap: spacing[3] },
  vistaImagen: { width: 96, height: 96, objectFit: "cover", borderRadius: radius.sm },
  aviso: {
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  avisoLista: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[2],
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  listaHuecos: { margin: 0, paddingLeft: spacing[5], fontVariantNumeric: "tabular-nums" },
  info: {
    background: colors.infoLight,
    color: colors.infoDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  ok: {
    background: colors.successLight,
    color: colors.successDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  error: {
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
  errorConAcciones: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
    lineHeight: 1.5,
  },
};
